/* By My Word — "The Thread" · design tokens (web)
   Source of truth: design-spec/01-foundations.md

   TOKENS ARE TIERED BY PURPOSE (16 Jul 2026, founder-ratified — see 01-foundations.md):
     TEXT tier   — guaranteed >=4.5:1 (WCAG AA) on every paper/surface/tint in this palette.
                   ink · ink-70 · ink-quiet · sage-text · coral-text · gold-text
     GLYPH tier  — >=3:1, meets WCAG 1.4.11 for meaningful icons/state glyphs.  ink-52
     DECOR tier  — no contrast guarantee; never carries meaning on its own.
                   ink-38 · line · line-2 · the -deep/-tint hues used as fills & borders
   Putting a DECOR/GLYPH token on text is a bug. Putting a TEXT token on a fill is just wasteful. */
:root{
  /* neutrals */
  --paper:#FAF6F1;
  --paper-2:#F1E9DF;
  --paper-3:#E9DFD2;
  --porcelain:#FDFBF7;
  --surface:#FFFFFF;
  --surface-tint:#FDFAF6;
  --ink:#23201B;
  --ink-70:rgba(35,32,27,.70);
  /* The quietest text the palette allows. It is NOT a step lighter than ink-70 — it can't be:
     one alpha step below ink-70 (.66) already measures 4.45:1 on paper-2 and fails AA, so the
     luminance axis is exhausted. This is the same LIGHTNESS as ink-70 (L* 42.1 vs 40.9) held a
     long way round the CHROMA axis (C* 16.3 vs 3.3, dE76 12.9) — a warm sepia that shares
     paper's own hue family, so it settles into the page while neutral ink-70 sits on top of it.
     Tone-on-tone recession, not a contrast drop. 5.55:1 on paper, 4.67:1 worst-case (gold-tint). */
  --ink-quiet:#74604A;
  --ink-52:rgba(35,32,27,.52);   /* GLYPH tier — 3.33:1. Icons/state glyphs, never text. */
  --ink-38:rgba(35,32,27,.38);   /* DECOR tier — 2.29:1. Rules, hairlines, labelled dots. */
  --line:rgba(35,32,27,.10);
  --line-2:rgba(35,32,27,.16);
  --muted:#8A8375;               /* DECOR tier — 3.50:1. Fills only; use ink-quiet for text. */
  --muted-tint:#ECE7E0;
  --bezel:#1B1915;

  /* brand hues.
     Each hue has a *-deep (fills, borders, hover — DECOR) and a *-text (TEXT tier). They are
     different jobs: the -deep values are tuned to look right as a 40px pill of colour, and none
     of them survives as small text on its own tint (sage-deep on sage-tint 4.07:1, coral-deep on
     coral-tint 3.47:1). --gold-text already encoded this split; the other two now follow it. */
  --sage:#7A9278;
  --sage-deep:#5E7860;
  --sage-deep-hover:#4F6A52;
  --sage-tint:#E7EDE5;
  --sage-text:#576F58;           /* 5.11:1 paper · 4.62:1 sage-tint */
  --coral:#D9704A;
  --coral-deep:#C25B37;
  --coral-tint:#F7E2D7;
  --coral-text:#A34D2E;          /* 5.34:1 paper · 4.60:1 coral-tint. Error/attention text. */
  --peach:#F6E3D6;
  --gold:#C8892A;
  --gold-deep:#A76E1E;
  --gold-tint:#F1E2C4;
  --gold-text:#865C15;           /* was #8A5F16 — 4.40:1 on gold-tint, its own sanctioned
                                    pairing, failed AA. Now 4.62:1 there, 5.49:1 on paper. */

  /* gradients */
  --grad-display:linear-gradient(105deg,var(--sage-deep) 0%,var(--coral) 78%);
  --grad-thread:linear-gradient(90deg,#7A9278 0%,#E7A987 50%,#D9704A 100%);
  --grad-gold-cta:linear-gradient(135deg,#DAA43F,#C8892A);
  --grad-seal:radial-gradient(circle at 34% 30%,#E7B45C,#C8892A 55%,#A76E1E);
  --grad-footer:linear-gradient(135deg,#2C3B2D 0%,#3A4D3B 42%,#6D4433 100%);
  --grad-avatar-sage:linear-gradient(140deg,#7A9278,#5E7860);
  --grad-avatar-coral:linear-gradient(140deg,#D9704A,#C25B37);
  --grad-avatar-plum:linear-gradient(140deg,#8B7BB5,#6A5C92);
  --grad-avatar-deepsage:linear-gradient(140deg,#5E7860,#455A48);
  --grad-avatar-muted:linear-gradient(140deg,#B0A89A,#8A8172);
  --grad-card-ready:linear-gradient(180deg,#FEFBF3,#FBF4E4);

  /* type */
  --font-display:"Sora",system-ui,-apple-system,"Segoe UI",sans-serif;
  --font-body:"Inter",system-ui,-apple-system,"Segoe UI",sans-serif;
  --font-serif:Georgia,"Times New Roman",serif;
  --font-hand:"Caveat",cursive;

  /* radius */
  --r-sm:8px;
  --r-md:14px;
  --r-lg:20px;
  --r-xl:26px;
  --r-card:26px;
  --r-btn:100px;
  --r-sheet:28px;

  /* spacing */
  --sp-1:4px; --sp-2:8px; --sp-3:12px; --sp-4:16px; --sp-5:24px; --sp-6:32px; --sp-7:48px;

  /* shadows (warm) */
  --shadow-sm:0 2px 8px rgba(60,44,30,.06);
  --shadow-md:0 18px 44px -22px rgba(60,44,30,.30),0 4px 14px rgba(60,44,30,.06);
  --shadow-lg:0 46px 100px -46px rgba(60,44,30,.46),0 12px 34px rgba(60,44,30,.08);
  --shadow-sheet:0 -20px 44px -20px rgba(60,44,30,.42);
  --shadow-btn-sage:0 8px 20px -10px rgba(94,120,96,.7);
  --shadow-btn-coral:0 8px 20px -10px rgba(217,112,74,.7);
  --shadow-card-ready:0 8px 22px -14px rgba(200,137,42,.6);
  --shadow-seal:0 10px 20px -8px rgba(168,110,30,.75),inset 0 1px 5px rgba(255,255,255,.4);
  --shadow-porcelain:0 38px 84px -46px rgba(60,44,30,.34),0 6px 20px rgba(60,44,30,.05);

  /* motion */
  --ease-out:cubic-bezier(.22,.61,.36,1);
  --dur-ui:200ms;          /* 150–250ms band */
  --dur-ceremony:450ms;
  --wrap:1200px;
}
